// Review state for the editable plan (docs/superpowers/specs/2026-10-07-editable-plan-design.md,
// 1a "State"). Pure: no React, no Date, no randomness, no I/O. It never mutates its
// input. An action that applies returns a new object; one that does not apply (wrong
// stage, invalid payload, a row that fails validateRowParams) returns the identical
// state, so callers and tests can tell the two apart by identity.
import {
  MAX_ROW_CANDIDATES,
  cloneJson,
  rowComparable,
  runTotalItemForRow,
  validateRowParams,
} from '../ai/rows/rowContract';
import { chargeOf } from '../ai/router/fitRunTotal';
import { fitRows } from '../ai/rows/prefillRows';
import { BUILTIN_RANKING_WEIGHTS } from '../config/ranking';

export const REVIEW_SCHEMA_VERSION = 1;
export const REVIEW_STAGES = Object.freeze(['idle', 'understood', 'routed', 'manual']);
// Row actions apply only in these stages (contract 1.8 stage guard, D49). In
// 'understood' the rows are about to be replaced by ROUTED.
const ROW_EDIT_STAGES = Object.freeze(['routed', 'manual']);
// The only stages whose review is debounced to the draft run doc.
const DRAFT_STAGES = Object.freeze(['understood', 'routed']);

export const INITIAL_REVIEW_STATE = Object.freeze({
  stage: 'idle',
  // generation_spec after sanitizeNlMeta + resolveSpec + the plan-max clamp.
  decomposerSnapshot: null,
  // The (edited) generation_spec; the same content as decomposerSnapshot in 1a.
  spec: null,
  // { rows: Row[], skipped: Skipped[], reconstructed: boolean } | null, taken after
  // every automatic adjustment (weight, shares, snap, fit), so only expert edits differ.
  routerSnapshot: null,
  rows: Object.freeze([]),
  rankingWeights: BUILTIN_RANKING_WEIGHTS,
  proposedRankingWeights: BUILTIN_RANKING_WEIGHTS,
  proposedRankingSource: 'builtin',
  // Array<{ index, name }>, index into the routed spec.targets (1c fills it; [] in 1a).
  unsteerableTargets: Object.freeze([]),
  // 'chip' | 'classifier' | 'keyword_fallback' | null
  classifierSource: null,
});

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// A deep copy of `value`, or `fallback` when the payload left the field out.
function copyOr(value, fallback) {
  return value === undefined ? fallback : cloneJson(value);
}

// The row-writing rule (contract 1.8, D41) over one list of rows: a fresh deep copy
// in which every represent row's candidates equals its seed count, or null when any
// row fails validateRowParams or two rows share an id.
function writeRows(rows) {
  if (!Array.isArray(rows)) return null;
  const next = cloneJson(rows);
  const ids = new Set();
  for (const row of next) {
    if (isPlainObject(row) && row.kind === 'represent'
      && isPlainObject(row.params) && Array.isArray(row.params.seed_smiles)) {
      row.candidates = row.params.seed_smiles.length;
    }
    if (!validateRowParams(row).ok || ids.has(row.id)) return null;
    ids.add(row.id);
  }
  return next;
}

// A router snapshot under the row-writing rule, in its { rows, skipped, reconstructed }
// shape, or null when it is not an object or one of its rows fails the rule.
function writeRouterSnapshot(snapshot) {
  if (!isPlainObject(snapshot)) return null;
  const rows = writeRows(snapshot.rows);
  if (!rows) return null;
  return {
    rows,
    skipped: Array.isArray(snapshot.skipped) ? cloneJson(snapshot.skipped) : [],
    reconstructed: snapshot.reconstructed === true,
  };
}


// ── The run-total fit after a row edit (1b-1, D-1b1-2 and D-1b1-3) ───────────
// Step 2 keeps the run total inside the plan max, using the same fitRows the prefill
// uses, so one place decides a fitted count and writes adjustments.scaledFrom.
//
// Two differences from Route. The plan max travels in the action, because a reducer
// cannot read context; when it is absent the fit is skipped and prepareRows still fits
// as a backstop at Execute. And a fit that would DROP a row is refused: at Route a
// dropped row is reported as plan_max, but a row the user just added or swapped must not
// silently vanish, so the whole action returns the identical state instead.
//
// Returns the fitted rows, or null when the fit would drop one.
function fitOrRefuse(rows, targets, planMax) {
  const max = Number(planMax);
  if (!Number.isFinite(max) || max <= 0) return rows;
  const fitted = fitRows(rows, targets, max);
  return fitted.length === rows.length ? fitted : null;
}

// The fit for an edit that can only LOWER the total, which is removal. Refusing here
// would be indefensible: the user is reducing the plan and would be told nothing. Worse,
// it is self-locking. A plan restored under a smaller plan max (a draft authored under a
// larger plan, or an org downgrade) can have every single removal refused, because the
// rows that remain still do not fit, so Add is blocked and Remove is inert and the only
// exit is Start over. The removal always lands; the fit is best effort.
function fitBestEffort(rows, targets, planMax) {
  const max = Number(planMax);
  if (!Number.isFinite(max) || max <= 0) return rows;
  const fitted = fitRows(rows, targets, max);
  return fitted.length === rows.length ? fitted : rows;
}

// One row action: validate the next rows under the row-writing rule, fit them, and
// return the new state, or the identical state when any step refuses.
function withRows(state, nextRows, planMax, { refuseOnDrop = true } = {}) {
  const written = writeRows(nextRows);
  if (!written) return state;
  const targets = state.spec && state.spec.targets;
  const fitted = refuseOnDrop
    ? fitOrRefuse(written, targets, planMax)
    : fitBestEffort(written, targets, planMax);
  if (!fitted) return state;
  return { ...state, rows: fitted };
}

export function reviewReducer(state = INITIAL_REVIEW_STATE, action) {
  switch (action && action.type) {
    case 'RESET':
      return INITIAL_REVIEW_STATE;

    case 'UNDERSTOOD':
      // From any stage: a new plan was understood, so nothing routed survives.
      return {
        ...INITIAL_REVIEW_STATE,
        stage: 'understood',
        decomposerSnapshot: copyOr(action.decomposerSnapshot, null),
        spec: copyOr(action.spec, null),
        rankingWeights: copyOr(action.rankingWeights, BUILTIN_RANKING_WEIGHTS),
        proposedRankingWeights: copyOr(action.proposedRankingWeights, BUILTIN_RANKING_WEIGHTS),
        proposedRankingSource: typeof action.proposedRankingSource === 'string' ? action.proposedRankingSource : 'builtin',
        classifierSource: action.classifierSource ?? null,
        rows: [],
        routerSnapshot: null,
        unsteerableTargets: [],
      };

    case 'ROUTED': {
      // Only from 'understood'; spec, rows and the router snapshot are all required.
      if (state.stage !== 'understood' || !isPlainObject(action.spec)) return state;
      const rows = writeRows(action.rows);
      const routerSnapshot = writeRouterSnapshot(action.routerSnapshot);
      if (!rows || !routerSnapshot) return state;
      return {
        ...state,
        stage: 'routed',
        spec: cloneJson(action.spec),
        routerSnapshot,
        rows,
        unsteerableTargets: Array.isArray(action.unsteerableTargets) ? cloneJson(action.unsteerableTargets) : [],
      };
    }

    case 'SET_ROW_CANDIDATES': {
      if (!ROW_EDIT_STAGES.includes(state.stage)) return state;
      const n = action.candidates;
      if (!Number.isInteger(n) || n < 1 || n > MAX_ROW_CANDIDATES) return state;
      const index = state.rows.findIndex((row) => row.id === action.rowId);
      // A represent row's count is its seed count; it is never edited here.
      if (index < 0 || state.rows[index].kind === 'represent') return state;
      // An expert count replaces any "rounded from" / "scaled to fit" note.
      const rows = writeRows(state.rows.map((row, i) => (i === index ? { ...row, candidates: n, adjustments: {} } : row)));
      return rows ? { ...state, rows } : state;
    }

    case 'REMOVE_ROW': {
      if (!ROW_EDIT_STAGES.includes(state.stage)) return state;
      if (typeof action.rowId !== 'string') return state;
      if (!state.rows.some((row) => row.id === action.rowId)) return state;
      // Removal only lowers the total, so the fit here can never scale anything up; it
      // runs anyway for the restored-under-a-smaller-plan case, where what is LEFT can
      // still be over the max. It never refuses: see fitBestEffort.
      return withRows(state, state.rows.filter((row) => row.id !== action.rowId), action.planMax, { refuseOnDrop: false });
    }

    case 'ADD_ROW': {
      if (!ROW_EDIT_STAGES.includes(state.stage)) return state;
      if (!isPlainObject(action.row) || typeof action.row.id !== 'string') return state;
      // writeRows refuses a duplicate id too; checking here keeps the reason local.
      if (state.rows.some((row) => row.id === action.row.id)) return state;
      return withRows(state, [...state.rows, action.row], action.planMax);
    }

    case 'SWAP_ROW': {
      if (!ROW_EDIT_STAGES.includes(state.stage)) return state;
      if (typeof action.rowId !== 'string') return state;
      if (!isPlainObject(action.row) || typeof action.row.id !== 'string') return state;
      // rowId says WHICH row is being replaced and action.row is the replacement, and
      // the two must agree. Identifying the row by the replacement's own id alone would
      // mean a replacement built against the wrong row silently swaps that other row
      // instead, with everything still valid. A swap keeps the id (D-1b1-4), so a
      // mismatch is a caller bug, not a rename.
      if (action.row.id !== action.rowId) return state;
      const index = state.rows.findIndex((row) => row.id === action.rowId);
      if (index < 0) return state;
      return withRows(state, state.rows.map((row, i) => (i === index ? action.row : row)), action.planMax);
    }

    case 'SET_ROW_PARAM': {
      // The Advanced edit: one param key at a time, revalidated by the row-writing rule,
      // which is what rejects a key the service does not allow, a key this row's target
      // does not allow, and a value outside the contract's bounds.
      if (!ROW_EDIT_STAGES.includes(state.stage)) return state;
      if (typeof action.rowId !== 'string' || typeof action.key !== 'string') return state;
      // undefined would DELETE the key, because writeRows deep-copies through JSON. That
      // is a second, undocumented meaning for this action, so it is refused instead.
      if (action.value === undefined) return state;
      const index = state.rows.findIndex((row) => row.id === action.rowId);
      if (index < 0) return state;
      const next = state.rows.map((row, i) => (
        i === index ? { ...row, params: { ...row.params, [action.key]: action.value } } : row
      ));
      // An Advanced edit cannot change a candidate count, but it CAN change the charge
      // the plan max bounds: oversample_multiplier multiplies an FM4M row's charge, so
      // one edit took a 60-candidate run to 200 with no fit and no label. A seed-list
      // edit changes the count itself, and writeRows recomputes it from the seeds.
      return withRows(state, next, action.planMax);
    }

    case 'RESTORE': {
      // The payload comes from restoreReviewState or rebuildLegacyReview (already
      // validated); the row-writing rule runs again, and the result is always a
      // fresh object, so a restore re-renders even when the content is unchanged.
      const src = action.state;
      if (!isPlainObject(src) || !REVIEW_STAGES.includes(src.stage)) return state;
      const rows = writeRows(src.rows === undefined ? [] : src.rows);
      if (!rows) return state;
      let routerSnapshot = null;
      if (src.routerSnapshot != null) {
        routerSnapshot = writeRouterSnapshot(src.routerSnapshot);
        if (!routerSnapshot) return state;
      }
      return {
        stage: src.stage,
        decomposerSnapshot: copyOr(src.decomposerSnapshot, null),
        spec: copyOr(src.spec, null),
        routerSnapshot,
        rows,
        rankingWeights: copyOr(src.rankingWeights, BUILTIN_RANKING_WEIGHTS),
        proposedRankingWeights: copyOr(src.proposedRankingWeights, BUILTIN_RANKING_WEIGHTS),
        proposedRankingSource: typeof src.proposedRankingSource === 'string' ? src.proposedRankingSource : 'builtin',
        unsteerableTargets: Array.isArray(src.unsteerableTargets) ? cloneJson(src.unsteerableTargets) : [],
        classifierSource: src.classifierSource ?? null,
      };
    }

    default:
      return state;
  }
}

// ── Selectors ────────────────────────────────────────────────────────────────

// Deep equality of two JSON values; object key order is ignored, array order is not.
function jsonEqual(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, i) => jsonEqual(value, b[i]));
  }
  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && jsonEqual(a[key], b[key]));
}

function sameRow(a, b) {
  return jsonEqual(rowComparable(a), rowComparable(b));
}

/** The run total the rows charge (fitRunTotal's chargeOf); represent rows count 0. */
export function selectRunTotal(state) {
  const targets = state?.spec?.targets;
  return (state?.rows || []).reduce((sum, row) => sum + chargeOf(runTotalItemForRow(row, targets)), 0);
}

/** "Changed from router" for one row: false without a router snapshot or for an id
 * that is not in rows; true when the id is absent from the snapshot or the two rows'
 * rowComparable values differ (object key order ignored). */
export function selectRowChanged(state, rowId) {
  const snapshot = state?.routerSnapshot;
  if (!snapshot) return false;
  const row = (state.rows || []).find((r) => r.id === rowId);
  if (!row) return false;
  const original = (snapshot.rows || []).find((r) => r.id === rowId);
  return !original || !sameRow(row, original);
}

/** Whether any row differs from the router snapshot: the id sets differ (order
 * ignored), or a shared id's rowComparable values differ. False without a snapshot. */
export function selectRowsChanged(state) {
  const snapshot = state?.routerSnapshot;
  if (!snapshot) return false;
  const rows = state.rows || [];
  const original = new Map((snapshot.rows || []).map((r) => [r.id, r]));
  if (rows.length !== original.size) return true;
  return rows.some((row) => !original.has(row.id) || !sameRow(row, original.get(row.id)));
}

/** Execute is possible in routed or manual with at least one row. */
export function selectCanExecute(state) {
  return ROW_EDIT_STAGES.includes(state?.stage) && Array.isArray(state.rows) && state.rows.length > 0;
}

/** The only stages whose review is debounced to the draft doc. */
export function selectIsDraft(state) {
  return DRAFT_STAGES.includes(state?.stage);
}

/** Whether adding THIS row would be refused for lack of room at this plan max
 * (D-1b1-2). It runs the same fit ADD_ROW runs, on the same row, so the two cannot give
 * different answers. The UI uses it to disable Add and say why, instead of relying on the
 * identical-state signal, which cannot distinguish no-room from an invalid row.
 *
 * It takes the row because the question is not answerable without it. A probe row stood
 * in for "the cheapest possible step" and was wrong twice over: an FM4M row's charge is
 * its count times its oversample, which comes from the spec filters and is 3 on any row
 * with a bound, and a MatterGen row's minimum is 8 rather than 1. So the selector
 * reported room where ADD_ROW refused, and Add did nothing with nothing said.
 *
 * False without a plan max (nothing is fitted) or without a row (nothing is claimed). */
export function selectAddRowBlocked(state, planMax, row) {
  const max = Number(planMax);
  if (!Number.isFinite(max) || max <= 0) return false;
  if (row === null || typeof row !== 'object' || Array.isArray(row)) return false;
  const rows = (state && state.rows) || [];
  return fitOrRefuse([...rows, row], state && state.spec && state.spec.targets, max) === null;
}
